import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import AdventureCard from "../components/AdventureCard";
import { TopBar } from "../components/Navigation";
import LevelBadge from "../components/LevelBadge";
import PassportShowcase from "../components/PassportShowcase";
import {
    MapPin, Mountain, Edit2, UserPlus, UserCheck, Share2, MessageCircle,
    Lock, Eye, Crown, Award, Camera, Backpack, Star, Compass,
    Image as ImageIcon, Bookmark, Calendar, Sparkles, Globe, Stamp, BadgeCheck, CloudOff,
} from "lucide-react";
import { toast } from "sonner";
import ShareModal from "../components/ShareModal";

function partnerSinceShort(iso) {
    if (!iso) return "today";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    } catch { return "today"; }
}
import InviteToAdventureModal from "../components/InviteToAdventureModal";
import ReviewsBlock from "../components/ReviewsBlock";
import { MarketplaceGearGrid } from "../components/MarketplaceGearPicker";
import OfflineGuidesShelf from "../components/OfflineGuidesShelf";
import { gam } from "../lib/gamificationApi";
import { interestLabel, STYLE_LABEL } from "../lib/interests";

const styleText = (p) =>
    (p.adventure_styles?.length
        ? p.adventure_styles.map((s) => STYLE_LABEL[s] || s).join(", ")
        : (p.adventure_style || ""));

const TABS = [
    { id: "adventures", label: "Adventures", icon: Mountain },
    { id: "passport", label: "Passport", icon: Stamp },
    { id: "photos", label: "Photos", icon: Camera },
    { id: "gear", label: "Gear", icon: Backpack },
    { id: "reviews", label: "Reviews", icon: Star },
    { id: "saved", label: "Saved", icon: Bookmark, ownerOrPublic: true },
    { id: "offline", label: "Offline", icon: CloudOff, ownerOnly: true },
    { id: "badges", label: "Badges", icon: Award },
];

export default function Profile() {
    const { userId } = useParams();
    const { user: me } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const targetId = userId || me?.user_id;
    const isMe = !userId || userId === me?.user_id;
    const viewAsPublic = searchParams.get("view") === "public";

    const [profile, setProfile] = useState(null);
    const [stats, setStats] = useState(null);
    const [badges, setBadges] = useState([]);
    const [adventures, setAdventures] = useState([]);
    const [saved, setSaved] = useState([]);
    const [tab, setTab] = useState("adventures");
    const [loading, setLoading] = useState(true);
    const [following, setFollowing] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [messaging, setMessaging] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [level, setLevel] = useState(null);
    const [passport, setPassport] = useState(null);
    const [partnerInfo, setPartnerInfo] = useState(null);

    useEffect(() => {
        if (!targetId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [u, advs, st, bg, lvl, pp, partner] = await Promise.all([
                    api.get(`/users/${targetId}`),
                    api.get(`/adventures`, { params: { author_id: targetId, limit: 100 } }).catch(() => ({ data: [] })),
                    api.get(`/users/${targetId}/stats`).catch(() => ({ data: null })),
                    api.get(`/users/${targetId}/badges`).catch(() => ({ data: { badges: [] } })),
                    gam.level(targetId).catch(() => null),
                    gam.passport(targetId).catch(() => null),
                    api.get(`/brands/${targetId}`).catch(() => null),
                ]);
                if (cancelled) return;
                setProfile(u.data);
                setAdventures(advs.data || []);
                setStats(st.data);
                setBadges(bg.data?.badges || []);
                setLevel(lvl);
                setPassport(pp);
                setPartnerInfo(partner?.data || null);
                if (isMe) {
                    try {
                        const s = await api.get("/users/me/saved");
                        if (!cancelled) setSaved(s.data);
                    } catch { /* ignore */ }
                } else if (me) {
                    try {
                        const f = await api.get(`/users/${targetId}/is-following`);
                        if (!cancelled) setFollowing(f.data.following);
                    } catch { /* ignore */ }
                }
            } catch {
                toast.error("Profile not found");
                navigate("/explore");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [targetId, isMe, me, navigate]);

    const toggleFollow = async () => {
        if (!me) { navigate("/login"); return; }
        try {
            const { data } = await api.post(`/users/${targetId}/follow`);
            setFollowing(data.following);
            setProfile((p) => p ? { ...p, followers_count: (p.followers_count || 0) + (data.following ? 1 : -1) } : p);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const startMessage = async () => {
        if (!me) { navigate("/login"); return; }
        setMessaging(true);
        try {
            const { data } = await api.post("/messages/threads", { recipient_user_id: targetId });
            navigate(`/messages/${data.id}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setMessaging(false);
        }
    };

    const inviteToAdventure = () => setInviteOpen(true);

    const photos = useMemo(() => {
        const all = [];
        adventures.forEach((a) => (a.photos || []).forEach((p) => all.push({ url: p, advId: a.id, title: a.title })));
        return all;
    }, [adventures]);

    const ownerView = isMe && !viewAsPublic;
    const showLocked = profile?._locked;

    if (loading) return <ProfileSkeleton />;
    if (!profile) return null;

    const allowMessage = !isMe && me && (
        profile.message_permissions === "everyone" ||
        (profile.message_permissions === "followers" && following)
    );
    const showInvite = !isMe && me && profile.open_to_invites;

    return (
        <div data-testid="profile-page">
            <TopBar title={profile.name || "Profile"} />

            {/* HERO */}
            <ProfileHero
                profile={profile}
                badges={badges}
                level={level}
                partnerInfo={partnerInfo}
                ownerView={ownerView}
                viewAsPublic={viewAsPublic}
                following={following}
                allowMessage={allowMessage}
                showInvite={showInvite}
                onShare={() => setShareOpen(true)}
                onFollow={toggleFollow}
                onMessage={startMessage}
                onInvite={inviteToAdventure}
                onViewAsPublic={() => setSearchParams({ view: "public" })}
                onExitViewAsPublic={() => setSearchParams({})}
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
                {showLocked ? (
                    <LockedView profile={profile} onFollow={toggleFollow} me={me} />
                ) : (
                    <>
                        {/* STATS */}
                        <StatsRow stats={stats} profile={profile} />

                        {/* GRID — main + sidebar */}
                        <div className="grid lg:grid-cols-[1fr_320px] gap-8 mt-8">
                            {/* MAIN */}
                            <div className="min-w-0">
                                {ownerView && <PrivacyBanner profile={profile} />}

                                {/* Tabs */}
                                <div data-testid="profile-tabs" className="border-b border-ridge-line flex items-center gap-1 overflow-x-auto mb-6 sticky top-16 lg:top-0 bg-alpine-mist z-10 lg:bg-transparent">
                                    {TABS.filter((t) => {
                                        if (t.ownerOnly && !isMe) return false;
                                        if (t.id === "saved") {
                                            if (isMe) return true;
                                            if (profile.saved_visibility === "public") return true;
                                            if (profile.saved_visibility === "followers" && following) return true;
                                            return false;
                                        }
                                        if (t.id === "gear" && profile.hide_gear_details && !isMe) return false;
                                        return true;
                                    }).map((t) => {
                                        const Icon = t.icon;
                                        const active = tab === t.id;
                                        return (
                                            <button
                                                key={t.id}
                                                data-testid={`tab-${t.id}`}
                                                onClick={() => setTab(t.id)}
                                                className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition-all whitespace-nowrap ${
                                                    active
                                                        ? "border-ember-orange text-ember-orange"
                                                        : "border-transparent text-slate-gray hover:text-granite"
                                                }`}
                                            >
                                                <Icon className="w-4 h-4" /> {t.label}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Tab content */}
                                {tab === "adventures" && <AdventuresTab adventures={adventures} />}
                                {tab === "passport" && <PassportShowcase passport={passport} />}
                                {tab === "photos" && <PhotosTab photos={photos} />}
                                {tab === "gear" && <GearTab profile={profile} ownerView={ownerView} />}
                                {tab === "reviews" && <ReviewsByUserTab authorId={profile.user_id} authorName={profile.name} />}
                                {tab === "saved" && (
                                    isMe ? <AdventuresTab adventures={saved} emptyText="You haven't saved any adventures yet." />
                                         : <AdventuresTab adventures={saved} emptyText="Saved adventures are private." />
                                )}
                                {tab === "badges" && <BadgesTab badges={badges} certifications={profile.certifications || []} />}
                                {tab === "offline" && isMe && <OfflineGuidesShelf />}
                            </div>

                            {/* SIDEBAR */}
                            <aside className="space-y-4">
                                {level && <LevelBadge levelData={level} variant="sidebar" testIdPrefix="sidebar-level" />}
                                <IdentityPanel profile={profile} />
                                <ExperiencePanel profile={profile} />
                                {(!profile.hide_gear_details || isMe) && <GearPanel profile={profile} compact />}
                                <BadgesPanel badges={badges} />
                            </aside>
                        </div>
                    </>
                )}
            </div>

            <ShareModal
                open={shareOpen}
                onOpenChange={setShareOpen}
                kind="profile"
                target={profile}
                title={`Check out ${profile.name} on RealWorld Outdoors`}
            />
            <InviteToAdventureModal
                recipient={profile}
                open={inviteOpen}
                onOpenChange={setInviteOpen}
            />
        </div>
    );
}

// ---------- HERO ----------
function ProfileHero({
    profile, badges, level, partnerInfo, ownerView, viewAsPublic, following,
    allowMessage, showInvite, onShare, onFollow, onMessage, onInvite,
    onViewAsPublic, onExitViewAsPublic,
}) {
    const cover = profile.cover || "https://images.unsplash.com/photo-1585094500201-1a2655fc8241?w=2400";
    const initials = (profile.name || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
    return (
        <div data-testid="profile-hero" className="relative">
            {viewAsPublic && (
                <div className="bg-ember-orange text-white text-center py-2 text-xs font-bold uppercase tracking-widest">
                    Viewing as public — <button onClick={onExitViewAsPublic} className="underline" data-testid="exit-view-as-public">exit</button>
                </div>
            )}
            <div className="relative h-56 sm:h-64 lg:h-80 bg-midnight-pine overflow-hidden">
                <img src={cover} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4 -mt-16 sm:-mt-20 relative">
                    <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full overflow-hidden bg-trail-tan border-4 border-snowcap shadow-xl flex items-center justify-center text-midnight-pine font-display font-black text-5xl shrink-0">
                        {profile.avatar
                            ? <img src={profile.avatar} alt={profile.name} className="w-full h-full object-cover" data-testid="profile-avatar" />
                            : <span data-testid="profile-avatar-initials">{initials}</span>
                        }
                    </div>
                    <div className="flex-1 min-w-0 sm:pt-20 lg:pt-24">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 data-testid="profile-name" className="font-display font-black text-3xl sm:text-4xl text-midnight-pine tracking-tighter">
                                {profile.name}
                            </h1>
                            {profile.tier === "premium" && (
                                <span data-testid="profile-premium-badge" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-ember-orange text-white text-[10px] font-bold uppercase tracking-widest">
                                    <Crown className="w-3 h-3" /> Premium
                                </span>
                            )}
                            {styleText(profile) && (
                                <span data-testid="profile-style-badge" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-midnight-pine text-white text-[10px] font-bold uppercase tracking-widest">
                                    <Compass className="w-3 h-3" /> {styleText(profile)}
                                </span>
                            )}
                            {profile.looking_for_partners && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-moss/15 text-moss text-[10px] font-bold uppercase tracking-widest">
                                    <Sparkles className="w-3 h-3" /> Looking for partners
                                </span>
                            )}
                            {level && <LevelBadge levelData={level} variant="inline" testIdPrefix="profile-level" />}
                            {partnerInfo && (
                                <Link
                                    to={`/brands/${profile.user_id}`}
                                    data-testid="profile-partner-badge"
                                    title={`${partnerInfo.name} — Partner since ${(partnerInfo.partner_since || "").slice(0, 10)}`}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-deep-evergreen text-white text-[10px] font-bold uppercase tracking-widest hover:bg-deep-evergreen/90 transition-colors"
                                >
                                    <BadgeCheck className="w-3 h-3" /> Partner since {partnerSinceShort(partnerInfo.partner_since)}
                                </Link>
                            )}
                        </div>
                        {profile.username && (
                            <div data-testid="profile-username" className="text-sm font-semibold text-slate-gray">
                                @{profile.username}
                            </div>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-gray flex-wrap">
                            {profile.location && (
                                <span className="inline-flex items-center gap-1" data-testid="profile-location">
                                    <MapPin className="w-3.5 h-3.5" /> {profile.location}
                                </span>
                            )}
                            {profile.website && (
                                <a href={profile.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-ember-orange">
                                    <Globe className="w-3.5 h-3.5" /> Website
                                </a>
                            )}
                        </div>
                        {profile.bio && (
                            <p data-testid="profile-bio" className="mt-2 max-w-2xl text-granite text-sm leading-relaxed">
                                {profile.bio}
                            </p>
                        )}
                        {profile.interests?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-3">
                                {profile.interests.slice(0, 8).map((i) => (
                                    <span key={i} data-testid={`profile-interest-${i}`} className="px-2.5 py-0.5 rounded-full bg-trail-tan/60 text-midnight-pine text-[11px] font-bold uppercase tracking-wider">
                                        {interestLabel(i)}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    {/* ACTIONS */}
                    <div className="flex flex-wrap gap-2 sm:pt-20 lg:pt-24" data-testid="profile-actions">
                        {ownerView ? (
                            <>
                                <Link to="/profile/edit" data-testid="profile-edit-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-ember-orange text-white font-bold text-sm hover:bg-ember-dark active:scale-95 transition-all">
                                    <Edit2 className="w-4 h-4" /> Edit profile
                                </Link>
                                <button onClick={onViewAsPublic} data-testid="profile-view-public-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-snowcap border border-ridge-line text-granite font-bold text-sm hover:border-midnight-pine transition-all">
                                    <Eye className="w-4 h-4" /> View as public
                                </button>
                                <button onClick={onShare} data-testid="profile-share-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-snowcap border border-ridge-line text-granite font-bold text-sm hover:border-midnight-pine transition-all">
                                    <Share2 className="w-4 h-4" /> Share
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={onFollow}
                                    data-testid="profile-follow-btn"
                                    className={`inline-flex items-center gap-2 px-5 py-2 rounded-md font-bold text-sm transition-all active:scale-95 ${
                                        following
                                            ? "bg-snowcap border border-ridge-line text-granite hover:border-ember-orange"
                                            : "bg-ember-orange text-white hover:bg-ember-dark"
                                    }`}
                                >
                                    {following ? <><UserCheck className="w-4 h-4" /> Following</> : <><UserPlus className="w-4 h-4" /> Follow</>}
                                </button>
                                {allowMessage && (
                                    <button onClick={onMessage} data-testid="profile-message-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-snowcap border border-ridge-line text-granite font-bold text-sm hover:border-midnight-pine transition-all">
                                        <MessageCircle className="w-4 h-4" /> Message
                                    </button>
                                )}
                                {showInvite && (
                                    <button onClick={onInvite} data-testid="profile-invite-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-midnight-pine text-white font-bold text-sm hover:bg-deep-evergreen transition-all">
                                        <Sparkles className="w-4 h-4" /> Invite to adventure
                                    </button>
                                )}
                                <button onClick={onShare} data-testid="profile-share-btn" className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-snowcap border border-ridge-line text-granite font-bold text-sm hover:border-midnight-pine transition-all">
                                    <Share2 className="w-4 h-4" />
                                </button>
                            </>
                        )}
                    </div>
                </div>
                {profile.follow_request_behavior === "approval" && !ownerView && !following && (
                    <p className="text-xs text-slate-gray mt-2 italic">This profile requires follow approval.</p>
                )}
            </div>
        </div>
    );
}

// ---------- LOCKED ----------
function LockedView({ profile, onFollow, me }) {
    return (
        <div data-testid="locked-profile-view" className="max-w-md mx-auto mt-12 text-center bg-snowcap border border-ridge-line rounded-lg p-8">
            <Lock className="w-10 h-10 text-slate-gray mx-auto mb-3" />
            <h3 className="font-display font-black text-xl text-midnight-pine mb-2">This profile is private</h3>
            <p className="text-sm text-slate-gray mb-4">
                You'll need to {profile.follow_request_behavior === "approval" ? "send a follow request" : "follow"} to see {profile.name}'s adventures.
            </p>
            {me && (
                <button onClick={onFollow} data-testid="locked-follow-btn" className="px-5 py-2 rounded-md bg-ember-orange text-white font-bold text-sm hover:bg-ember-dark transition-all">
                    {profile.follow_request_behavior === "approval" ? "Request to follow" : "Follow"}
                </button>
            )}
        </div>
    );
}

// ---------- STATS ----------
function StatsRow({ stats, profile }) {
    if (!stats) return null;
    const items = [
        { label: "Adventures", value: stats.adventures_shared, icon: Mountain },
        { label: "Miles Logged", value: stats.miles_logged, icon: Compass },
        { label: "Routes", value: stats.routes_completed, icon: MapPin },
        { label: "Photos", value: stats.photos_shared, icon: Camera },
        { label: "Followers", value: profile.followers_count || 0, icon: UserPlus },
        { label: "Following", value: profile.following_count || 0, icon: UserCheck },
    ];
    return (
        <div data-testid="profile-stats" className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-6">
            {items.map((it) => (
                <div key={it.label} className="bg-snowcap border border-ridge-line rounded-lg p-3 text-center">
                    <it.icon className="w-4 h-4 text-ember-orange mx-auto mb-1" />
                    <div className="font-display font-black text-2xl text-midnight-pine leading-tight">{it.value || 0}</div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray">{it.label}</div>
                </div>
            ))}
        </div>
    );
}

// ---------- PRIVACY BANNER (owner) ----------
function PrivacyBanner({ profile }) {
    const items = [];
    items.push(profile.hide_gear_details
        ? "Your gear details are hidden from public viewers."
        : "Your gear details are currently public.");
    items.push(profile.hide_exact_locations_default
        ? "Exact adventure locations are hidden by default."
        : "Adventure locations are public by default.");
    items.push(profile.allow_in_search
        ? "Your profile is searchable."
        : "Your profile is hidden from search.");
    items.push(`Saved items: ${profile.saved_visibility || "private"}.`);
    return (
        <div data-testid="privacy-banner" className="mb-6 p-3 rounded-md bg-trail-tan/30 border border-trail-tan/60 text-xs text-granite flex items-start gap-2">
            <Lock className="w-4 h-4 text-midnight-pine shrink-0 mt-0.5" />
            <div>
                {items.map((t, i) => (
                    <div key={i}>{t}</div>
                ))}
                <Link to="/profile/edit" className="text-ember-orange font-bold hover:underline">Update privacy settings →</Link>
            </div>
        </div>
    );
}

// ---------- TAB CONTENT ----------
function AdventuresTab({ adventures, emptyText = "No adventures shared yet." }) {
    if (adventures.length === 0) {
        return <Empty icon={Mountain} text={emptyText} />;
    }
    return (
        <div data-testid="tab-content-adventures" className="grid sm:grid-cols-2 gap-4">
            {adventures.map((a) => <AdventureCard key={a.id} adventure={a} />)}
        </div>
    );
}

function PhotosTab({ photos }) {
    if (photos.length === 0) return <Empty icon={Camera} text="No adventure photos yet. Share an adventure to start building your gallery." />;
    return (
        <div data-testid="tab-content-photos" className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {photos.map((p, i) => (
                <Link key={i} to={`/adventures/${p.advId}`} className="aspect-square rounded-md overflow-hidden bg-trail-tan group relative">
                    <img src={p.url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                </Link>
            ))}
        </div>
    );
}

function GearTab({ profile, ownerView }) {
    if (profile.hide_gear_details && !ownerView) return null;
    const sections = [
        { label: "Vehicle / access", value: profile.vehicle_access, kind: "text" },
        { label: "Camping setup", value: profile.camping_setup, kind: "chips" },
        { label: "Navigation tools", value: profile.navigation_tools, kind: "chips" },
        { label: "Safety gear", value: profile.safety_gear, kind: "chips" },
        { label: "Favorite gear", value: profile.favorite_gear, kind: "chips" },
        { label: "Wishlist", value: profile.gear_wishlist, kind: "chips" },
    ].filter((s) => s.kind === "text" ? !!s.value : (s.value?.length > 0));
    if (sections.length === 0) return <Empty icon={Backpack} text="No public gear setup yet." />;
    return (
        <div data-testid="tab-content-gear" className="space-y-4">
            {profile.hide_gear_details && ownerView && (
                <div className="text-xs italic text-slate-gray p-3 rounded-md bg-trail-tan/30">
                    Your gear details are hidden from public viewers.
                </div>
            )}
            {sections.map((s) => (
                <div key={s.label} className="bg-snowcap border border-ridge-line rounded-lg p-4">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray mb-2">{s.label}</div>
                    {s.kind === "text" ? (
                        <div className="text-base font-bold text-midnight-pine capitalize">{s.value}</div>
                    ) : (
                        <div className="flex flex-wrap gap-1.5">
                            {s.value.map((t) => (
                                <span key={t} className="px-3 py-1 rounded-full bg-trail-tan/60 text-midnight-pine text-xs font-semibold capitalize">{t}</span>
                            ))}
                        </div>
                    )}
                </div>
            ))}
            <div className="text-xs text-slate-gray italic px-3 py-2 rounded-md bg-trail-tan/30">
                Marketplace-linked gear and reviews coming soon.
            </div>
            {profile.marketplace_links?.length > 0 && (
                <div className="mt-3">
                    <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-gray mb-2">Gear linked from Marketplace</h4>
                    <MarketplaceGearGrid ids={profile.marketplace_links} />
                </div>
            )}
        </div>
    );
}

function ReviewsByUserTab({ authorId, authorName }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const { data } = await api.get("/reviews", { params: { author_id: authorId, limit: 50 } });
                if (!cancel) setItems(data.items);
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => { cancel = true; };
    }, [authorId]);
    if (loading) return <Empty icon={Star} text="Loading reviews…" />;
    if (!items.length) return <Empty icon={Star} text={`${authorName} hasn't written any reviews yet.`} />;
    return (
        <ul data-testid="tab-content-reviews" className="space-y-3">
            {items.map((r) => (
                <li key={r.id} className="bg-snowcap border border-ridge-line rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <div className="inline-flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-ember-orange text-ember-orange" : "text-ridge-line"}`} />
                            ))}
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-gray capitalize">on {r.subject_kind}</span>
                    </div>
                    {r.title && <div className="font-bold text-sm text-midnight-pine">{r.title}</div>}
                    {r.text && <p className="text-sm text-granite mt-1 leading-relaxed">{r.text}</p>}
                </li>
            ))}
        </ul>
    );
}

function ReviewsTab() {
    return <Empty icon={Star} text="No reviews yet. Gear, trail, and campsite reviews are coming soon." />;
}

function BadgesTab({ badges, certifications }) {
    if (!badges.length && !certifications.length) {
        return <Empty icon={Award} text="Badges will appear as you explore, share, and contribute." />;
    }
    return (
        <div data-testid="tab-content-badges" className="space-y-6">
            {certifications.length > 0 && (
                <div>
                    <h3 className="text-sm font-bold uppercase tracking-widest text-slate-gray mb-3">Certifications</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {certifications.map((c) => (
                            <div key={c} className="bg-snowcap border-2 border-moss/30 rounded-lg p-3 flex items-center gap-2">
                                <div className="w-8 h-8 rounded-md bg-moss/15 text-moss flex items-center justify-center"><Award className="w-4 h-4" /></div>
                                <span className="text-sm font-bold text-midnight-pine">{c}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {badges.length > 0 && (
                <div>
                    <h3 className="text-sm font-bold uppercase tracking-widest text-slate-gray mb-3">Earned badges</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {badges.map((b) => (
                            <div key={b} data-testid={`badge-card-${b.replace(/\s+/g, "-")}`} className="bg-snowcap border border-ridge-line rounded-lg p-3 flex items-center gap-2">
                                <div className="w-8 h-8 rounded-md bg-ember-orange/15 text-ember-orange flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
                                <span className="text-sm font-bold text-midnight-pine">{b}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------- SIDEBAR PANELS ----------
function IdentityPanel({ profile }) {
    const rows = [
        { label: "Adventure style", value: styleText(profile) },
        { label: "Difficulty", value: (profile.preferred_difficulty || []).join(", ") },
        { label: "Terrain", value: (profile.preferred_terrain || []).join(", ") },
        { label: "Season", value: (profile.favorite_season || []).join(", ") },
        { label: "Trip length", value: profile.typical_trip_length },
        { label: "Group", value: (profile.preferred_group_type || []).join(", ") },
    ].filter((r) => !!r.value);
    if (rows.length === 0) return null;
    return (
        <Panel icon={Compass} title="Adventure Identity" testid="identity-panel">
            <dl className="space-y-2 text-sm">
                {rows.map((r) => (
                    <div key={r.label} className="flex items-baseline justify-between gap-3">
                        <dt className="text-[10px] font-bold uppercase tracking-widest text-slate-gray shrink-0">{r.label}</dt>
                        <dd className="text-right text-granite font-semibold capitalize">{r.value}</dd>
                    </div>
                ))}
            </dl>
            {(profile.open_to_invites || profile.looking_for_partners) && (
                <div className="mt-3 pt-3 border-t border-ridge-line flex flex-wrap gap-1.5">
                    {profile.open_to_invites && <span className="px-2 py-0.5 rounded-full bg-ember-orange/15 text-ember-orange text-[10px] font-bold uppercase tracking-widest">Open to invites</span>}
                    {profile.looking_for_partners && <span className="px-2 py-0.5 rounded-full bg-moss/15 text-moss text-[10px] font-bold uppercase tracking-widest">Seeking partners</span>}
                </div>
            )}
        </Panel>
    );
}

function ExperiencePanel({ profile }) {
    const exp = profile.experience_by_activity || {};
    const entries = Object.entries(exp).filter(([, v]) => !!v);
    if (entries.length === 0 && !profile.years_experience && !profile.remote_comfort) return null;
    return (
        <Panel icon={Mountain} title="Experience" testid="experience-panel">
            {profile.years_experience > 0 && (
                <div className="mb-3 text-sm">
                    <span className="font-display font-black text-2xl text-midnight-pine">{profile.years_experience}</span>
                    <span className="text-slate-gray ml-2 text-xs font-bold uppercase tracking-widest">years outdoors</span>
                </div>
            )}
            {entries.length > 0 && (
                <ul className="space-y-1.5">
                    {entries.map(([k, v]) => (
                        <li key={k} className="flex items-center justify-between text-sm">
                            <span className="capitalize text-granite">{k}</span>
                            <span className="text-xs font-bold uppercase tracking-widest text-ember-orange">{v}</span>
                        </li>
                    ))}
                </ul>
            )}
            {profile.remote_comfort && (
                <div className="mt-3 pt-3 border-t border-ridge-line text-xs">
                    <span className="font-bold uppercase tracking-widest text-slate-gray">Off-grid comfort: </span>
                    <span className="font-bold text-midnight-pine capitalize">{profile.remote_comfort}</span>
                </div>
            )}
        </Panel>
    );
}

function GearPanel({ profile, compact }) {
    const counts = {
        gear: (profile.favorite_gear || []).length,
        camping: (profile.camping_setup || []).length,
        nav: (profile.navigation_tools || []).length,
        safety: (profile.safety_gear || []).length,
    };
    const total = Object.values(counts).reduce((s, n) => s + n, 0);
    if (total === 0 && !profile.vehicle_access) return null;
    return (
        <Panel icon={Backpack} title="Outdoor Setup" testid="gear-panel">
            {profile.vehicle_access && (
                <div className="text-sm">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-gray">Vehicle</span>
                    <div className="font-bold text-midnight-pine">{profile.vehicle_access}</div>
                </div>
            )}
            {compact && counts.gear + counts.safety > 0 && (
                <div className="text-xs text-slate-gray mt-2">
                    {counts.gear} fave items · {counts.safety} safety items
                </div>
            )}
        </Panel>
    );
}

function BadgesPanel({ badges }) {
    if (!badges?.length) return null;
    return (
        <Panel icon={Award} title="Badges" testid="badges-panel">
            <div className="flex flex-wrap gap-1.5">
                {badges.slice(0, 6).map((b) => (
                    <span key={b} className="px-2.5 py-1 rounded-full bg-ember-orange/15 text-ember-orange text-[11px] font-bold">
                        {b}
                    </span>
                ))}
            </div>
        </Panel>
    );
}

function Panel({ icon: Icon, title, children, testid }) {
    return (
        <section data-testid={testid} className="bg-snowcap border border-ridge-line rounded-xl p-5">
            <header className="flex items-center gap-2 mb-3">
                <Icon className="w-4 h-4 text-ember-orange" />
                <h3 className="font-display font-bold text-base text-midnight-pine">{title}</h3>
            </header>
            {children}
        </section>
    );
}

function Empty({ icon: Icon, text }) {
    return (
        <div className="bg-snowcap border border-ridge-line rounded-lg p-10 text-center">
            <Icon className="w-10 h-10 text-slate-gray mx-auto mb-3" />
            <p className="text-sm text-slate-gray max-w-sm mx-auto">{text}</p>
        </div>
    );
}

function ProfileSkeleton() {
    return (
        <div>
            <div className="h-64 bg-trail-tan animate-pulse" />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="w-32 h-32 rounded-full bg-trail-tan animate-pulse -mt-16 border-4 border-snowcap" />
                <div className="h-8 w-64 bg-trail-tan animate-pulse mt-4 rounded" />
                <div className="h-4 w-48 bg-trail-tan animate-pulse mt-2 rounded" />
            </div>
        </div>
    );
}
